import { Component, OnInit } from '@angular/core';
import { DataServiceService } from '../services/data-service.service';
import { Drivers, Locations, Trailers, Trucks } from '../interfaces';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../services/auth.service';

// Add these interfaces for type safety
interface EditableItem {
  truck_id?: number;
  trailer_id?: number;
  [key: string]: any;
}

@Component({
  selector: 'app-equipment',
  templateUrl: './equipment.component.html',
  styleUrl: './equipment.component.scss',
  providers: [MessageService]
})

export class EquipmentComponent implements OnInit {


  trucks: Trucks[] = [];
  trailers: Trailers[] = [];
  vanTrailers: Trailers[] = [];
  filteredVanTrailers: Trailers[] = [];
  locations: Locations[] = [];
  drivers: Drivers[] = []
  
  searchTerm: string = '';
  
  newTruckDialog: boolean = false;
  newTrailerDialog: boolean = false;
  truckForm!: FormGroup;
  trailerForm!: FormGroup;
  savingTruck: boolean = false;
  savingTrailer: boolean = false;
  statuses: string[] = [
    '', 
    'Driver Assigned', 
    'Dump Spare', 
    'Empty', 
    'Higrade', 
    'Loaded', 
    'Maintenance', 
    'OOS', 
    'Pneu Spare', 
    'Sea Salt', 
    'Spare', 
    'Van Storage'
  ];
  setupTypes: string[] = [
    'ARDAGH LIME',
    'BOTTOM DROP-LIME',
    'BTM DROP',
    'CONVEYOR',
    'DUMP',
    'GREDE DUST',
    'HOPPER BTM',
    'HOPPER-LIME',
    'PNEU-SALT',
    'PNEUMATIC',
    'PNEUMATIC-LIME',
    'PLASTIC',
    'PUP',
    'SODA ASH',
    'VAN',
    'WAUKESHA LIME'
  ]; // Might need to adjust these
  filteredTrucks: Trucks[] = [];
  truckSearchTerm: string = '';
  trailerSearchTerm: string = '';
  filteredTrailers: Trailers[] = [];
  isAdmin = false;
  hasAccess = false;
  editTruckDialog: boolean = false;
  editTrailerDialog: boolean = false;


  saltTrailerLocations: Locations[] = [];
  vanLocations: any[] = []
  truckLocations: any[] = []
  allTrailerLocations: any[] = []

  private driverMapTruck: Map<number, string> = new Map();
  private driverMapTrailer: Map<number, string> = new Map();

  clonedItem: EditableItem | null = null;
  editingItem: EditableItem | null = null;

  vanTrailerSearchTerm: string = '';

  selectedTruck: any = {
    truck_id: null,
    truck_number: '',
    location_name: '',
    status: '',
    email: ''
  };

  statusOptions: any[] = [];

  selectedTrailer: any = {
    trailer_id: null,
    trailer_number: '',
    setup: '',
    location_id: null,
    location_name: '',
    status: '',
    customer_id: null,
    customer_name: ''
  };

  // Helper method to determine if a trailer is a salt trailer
  isSaltTrailer(trailer: any): boolean {
    return trailer?.setup?.toUpperCase().includes('SALT') || false;
  }

  // Helper method to determine if a trailer is a van trailer
  isVanTrailer(trailer: any): boolean {
    return trailer?.setup?.toLowerCase() === 'van' || false;
  }

  isTruck(truck: any): boolean {
    return truck.truck_id ? true : false;
  }

  isTrailer(trailer: any): boolean {
    return trailer.trailer_id ? true : false;
  }



  // Get appropriate location options based on trailer type
  getLocationOptionsForTrailer(trailer: any): any[] {
    if (this.isSaltTrailer(trailer)) {
      return this.saltTrailerLocations;
    } else if (this.isVanTrailer(trailer)) {
      return this.vanLocations;
    } else {
      return this.locations.filter(loc => loc.location_type !== 'TRUCK'); // Regular trailers use customer locations
    }
  }

  // Get location options for trailer form based on setup type
  getLocationOptionsForTrailerForm(): any[] {
    const setup = this.trailerForm.value.setup;
    if (this.isSaltTrailer({setup})) {
      return this.saltTrailerLocations;
    } else if (this.isVanTrailer({setup})) {
      return this.vanLocations;
    } else {
      return this.locations.filter(loc => loc.location_type !== 'TRUCK');
    }
  }

  getLocationOptionsForTruck(): any[] {
    return this.locations.filter(loc => loc.location_type === 'TRUCK' || loc.location_type === 'BOTH');
  }

  // Helper methods for form binding
  getTrailerFormLocationValue(): any {
    // All trailers now use location_id
    return this.trailerForm.value.location_id;
  }

  setTrailerFormLocationValue(value: any): void {
    // All trailers now use location_id
    this.trailerForm.patchValue({ location_id: value, customer_id: null });
  }

  getSelectedTrailerLocationValue(): any {
    // All trailers now use location_id
    return this.selectedTrailer.location_id;
  }

  setSelectedTrailerLocationValue(value: any): void {
    // All trailers now use location_id
    this.selectedTrailer.location_id = value;
    this.selectedTrailer.customer_id = null;
  }

  constructor(
    private dataServiceService: DataServiceService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private authService: AuthService,
    private confirmationService: ConfirmationService
  ) {
    this.initializeForms();
  }

  ngOnInit() {
    // Subscribe to drivers updates
    this.dataServiceService.drivers$.subscribe(drivers => {
      this.drivers = drivers;
      this.initializeDriverMaps();
      // Force refresh of trucks display when drivers change
      if (this.trucks.length > 0) {
        this.filteredTrucks = [...this.trucks];
      }
    });

    // Subscribe to trucks updates
    this.dataServiceService.trucks$.subscribe(trucks => {
      this.trucks = trucks;
      this.filteredTrucks = [...this.trucks];
      // Sort trucks by truck_number on load
      // Reinitialize driver maps when trucks update
      this.initializeDriverMaps();
    });

    // Subscribe to trailers updates
    this.dataServiceService.trailers$.subscribe(trailers => {
      this.trailers = trailers; // Directly use the fetched trailers
      this.filteredTrailers = [...this.trailers]; // Initialize filtered trailers
      this.vanTrailers = this.getVanTrailers(); // This will update totalVanTrailers
      this.filteredVanTrailers = [...this.vanTrailers]; // Update filtered van trailers
    });

    // Initial data loads
    this.loadLocations();
    this.dataServiceService.getAllTrucks().subscribe();
    this.dataServiceService.getAllTrailers().subscribe(); // Fetch trailers
    this.dataServiceService.getDriverDetails().subscribe();

    // // Admin check
    this.authService.userProfile$.subscribe(user =>
      this.isAdmin = user?.roles?.includes('Admin') ?? false
    );

    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Dispatcher') || profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });
    
    // Convert statuses to option objects
    this.statusOptions = this.statuses.map(status => ({
      name: status || 'None',
      status: status
    }));
  }

  private initializeDriverMaps() {
    this.driverMapTruck.clear();
    this.driverMapTrailer.clear();

    this.drivers.forEach(driver => {
      if (driver.truck_id) {
        this.driverMapTruck.set(
          driver.truck_id,
          `${driver.first_name} ${driver.last_name}`
        );
      }
      if (driver.trailer_id) {
        this.driverMapTrailer.set(
          driver.trailer_id,
          `${driver.first_name} ${driver.last_name}`
        );
      }
    });
  }

  loadLocations() {
    // Load customer locations (used for regular trailers)
    this.dataServiceService.getLocations().subscribe(locations => {
      this.locations = locations.map((location: any) => ({
        location_id: location.location_id,
        location_name: location.name || location.location_name, // Handle both possible property names
        location_type: location.location_type // Include the location_type property
      }));
    });
    
    // Load salt locations (SALT or BOTH types) for salt trailers
    this.dataServiceService.getSaltLocations().subscribe(locations => {
      console.log('Raw salt locations data:', locations); // Debug log
      this.saltTrailerLocations = locations.map((location: any) => ({
        location_id: location.location_id,
        location_name: location.name || location.location_name // Handle both possible property names
      }));
      console.log('Mapped salt trailer locations:', this.saltTrailerLocations); // Debug log
    });

    // Load van locations (VAN or BOTH types) for van trailers
    this.dataServiceService.getVanLocations().subscribe(locations => {
      console.log('Raw van locations data:', locations); // Debug log
      this.vanLocations = locations.map((location: any) => ({
        location_id: location.location_id,
        location_name: location.name || location.location_name // Handle both possible property names
      }));
      console.log('Mapped van locations:', this.vanLocations); // Debug log
    });
  }

  loadEquipment() {
    // Load trucks and maintain driver mappings
    this.dataServiceService.getAllTrucks().subscribe();
    this.dataServiceService.trucks$.subscribe(trucks => {
        this.trucks = trucks;
        this.filteredTrucks = trucks;
        this.initializeDriverMaps();  // Update driver mappings after trucks update
    });

    // Load trailers
    this.dataServiceService.getAllTrailers().subscribe(trailers => {
        this.trailers = trailers;
        this.filteredTrailers = trailers;
        this.vanTrailers = this.getVanTrailers();
        this.filteredVanTrailers = [...this.vanTrailers];
        this.initializeDriverMaps();  // Update driver mappings after trailers update
    });
}
  getVansAtLocation(location: any): Trailers[] {
    // Use the predefined van locations
    return this.trailers.filter(trailer => 
      trailer.setup?.toLowerCase() === 'van' && 
      trailer.location_id === location.location_id
    );
  }

  updateVanLocation(trailer: Trailers, event: any) {
    const newLocationId = event.value;
    // Ensure the new location is one of our predefined van locations
    if (this.vanLocations.some(loc => loc.location_id === newLocationId)) {
      const updateData = {
        location_id: newLocationId,
        location_name: this.vanLocations.find(loc => loc.location_id === newLocationId)?.location_name
      };

      this.dataServiceService.updateTrailer(trailer.trailer_id, updateData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Updated',
            detail: `Location updated for trailer ${trailer.trailer_number}`
          });
        },
        error: (error) => {
          console.error('Update error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update location'
          });
        }
      });
    }
  }

    getLocationName(trailer?: any, truck?: any): string {
    if (this.isSaltTrailer(trailer)) {
      // Salt trailers use location_id/location_name
      const location = this.saltTrailerLocations.find((l: any) => l.location_id === trailer.location_id);
      return location?.location_name || '';
    } else if (this.isVanTrailer(trailer)) {
      // Van trailers use location_id/location_name
      const location = this.vanLocations.find((l: any) => l.location_id === trailer.location_id);
      return location?.location_name || '';
    } else if (this.isTruck(truck)) {
      // Trucks use location_id/location_name
      const location = this.locations.find((l: any) => l.location_id === trailer.location_id);
      return location?.location_name || '';
    } else {
      // Regular trailers use location_id/location_name (all locations except TRUCK)
      const location = this.locations.find((l: any) => l.location_id === trailer.location_id);
      return location?.location_name || '';
    }
  }

  getDriverName(equipId: number, type: 'truck' | 'trailer'): string {
    if (type === 'truck') {
      const driverName = this.driverMapTruck.get(equipId);
      return driverName || '';
    } else {
      const driverName = this.driverMapTrailer.get(equipId);
      return driverName || '';
    }
  }

  private initializeForms() {
    this.truckForm = this.fb.group({
      truck_number: ['', Validators.required],
      location_id: [null],
      status: ['']
    });

    this.trailerForm = this.fb.group({
      trailer_number: ['', Validators.required],
      setup: [''],
      location_id: [null],
      customer_id: [null],
      status: ['']
    });
  }

  openNewTruckDialog() {
    this.newTruckDialog = true;
    this.truckForm.reset({ status: '' });
  }

  openNewTrailerDialog() {
    this.newTrailerDialog = true;
    this.trailerForm.reset({ status: '' });
  }

  hideDialog() {
    this.newTruckDialog = false;
    this.newTrailerDialog = false;
    this.truckForm.reset();
    this.trailerForm.reset();
  }

  saveTruck() {
      this.savingTruck = true;
      this.dataServiceService.createTruck(this.truckForm.value).subscribe({
        next: () => {
          this.savingTruck = false;
          this.hideDialog();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Truck added successfully'
          });
        },
        error: (error) => {
          this.savingTruck = false;
          console.error('Error adding truck:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add truck'
          });
        }
      });
  }

  saveTrailer() {
      this.savingTrailer = true;
      this.dataServiceService.createTrailer(this.trailerForm.value).subscribe({
        next: () => {
          this.savingTrailer = false;
          this.hideDialog();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Trailer added successfully'
          });
        },
        error: (error) => {
          this.savingTrailer = false;
          console.error('Error adding trailer:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add trailer'
          });
        }
      });
  }

  filterTrucks() {
    if (!this.truckSearchTerm.trim()) {
      this.filteredTrucks = [...this.trucks];
      return;
    }

    const search = this.truckSearchTerm.toLowerCase();
    this.filteredTrucks = this.trucks.filter(truck =>
      truck.truck_number.toLowerCase().includes(search)
    );
  }

  filterTrailers() {
    if (!this.trailerSearchTerm.trim()) {
      this.filteredTrailers = [...this.trailers];
      return;
    }

    const search = this.trailerSearchTerm.toLowerCase();
    this.filteredTrailers = this.trailers.filter(trailer =>
      trailer.trailer_number.toLowerCase().includes(search)
    );
  }

  filterVanTrailers(): void {
    // Filter van trailers based on search term
    this.vanLocations.forEach(location => {
      const vansAtLocation = this.getVansAtLocation(location);
      this.filteredVanTrailers = vansAtLocation.filter(van => 
        van.trailer_number.toLowerCase().includes(this.vanTrailerSearchTerm.toLowerCase())
      );
    });
  }

  getFilteredVansAtLocation(location: any): any[] {
    // Get vans at the specific predefined location
    const vansAtLocation = this.getVansAtLocation(location);
    return vansAtLocation
      .filter(van =>
      van.trailer_number.toLowerCase().includes(this.vanTrailerSearchTerm.toLowerCase())
      )
      .sort((a, b) => {
      const numA = parseInt(a.trailer_number, 10) || 0;
      const numB = parseInt(b.trailer_number, 10) || 0;
      return numA - numB;
      });
  }

  totalVanTrailers = 0;
  getVanTrailers(): Trailers[] {
    const vanTrailers = this.trailers.filter(trailer => trailer.setup?.toLowerCase() === 'van');
    this.totalVanTrailers = vanTrailers.length;
    console.log('Total van trailers:', this.totalVanTrailers); // Add this for debugging
    return vanTrailers;
  }


  onRowEditInit(item: EditableItem) {
    if (item.truck_id) {
      this.selectedTruck = { ...item };
      this.truckForm.patchValue(this.selectedTruck);
      this.editTruckDialog = true;
    } else {
      console.log('Editing trailer:', item);
      this.selectedTrailer = { ...item };  // Set the selectedTrailer
      this.trailerForm.patchValue(this.selectedTrailer);  // Patch the form values
      this.editTrailerDialog = true;
    }
  }

  onRowEditSave(equipment: any) {
    if (equipment.truck_id) {
      // For trucks
      // const location = this.locations.find(loc => loc.location_id === equipment.location_id);
      const updateData = {
        location_id: equipment.location_id,
        // location_name: location?.location_name || 'No Location',
        status: equipment.status,
        email: equipment.email
      };

      this.dataServiceService.updateTruck(equipment.truck_id, updateData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Truck updated successfully'
          });
        },
        error: (error) => {
          console.error('Update error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update truck'
          });
        }
      });
      this.editTruckDialog = false;
    } else {
      // For trailers - use appropriate fields based on trailer type
      let updateData: any = {
        status: equipment.status,
        setup: equipment.setup,
        email: equipment.email
      };
      
      if (this.isSaltTrailer(equipment)) {
        // Salt trailers use location_id/location_name
        const location = this.saltTrailerLocations.find((l: any) => l.location_id === equipment.location_id);
        updateData = {
          ...updateData,
          location_id: equipment.location_id,
          location_name: location?.location_name || 'No Location'
        };
      } else if (this.isVanTrailer(equipment)) {
        // Van trailers use location_id/location_name
        const location = this.vanLocations.find((l: any) => l.location_id === equipment.location_id);
        updateData = {
          ...updateData,
          location_id: equipment.location_id,
          location_name: location?.location_name || 'No Location'
        };
      } else {
        // Regular trailers use location_id/location_name (all locations except TRUCK)
        const location = this.locations.find((l: any) => l.location_id === equipment.location_id);
        updateData = {
          ...updateData,
          location_id: equipment.location_id,
          location_name: location?.location_name || 'No Location'
        };
      }

      this.dataServiceService.updateTrailer(equipment.trailer_id, updateData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Trailer updated successfully'
          });
        },
        error: (error) => {
          console.error('Update error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update trailer'
          });
        }
      });
      this.editTrailerDialog = false;
    }
  }

  onRowEditCancel(item: EditableItem) {
    if (this.clonedItem) {
      Object.assign(item, this.clonedItem);
    }
    this.editingItem = null;
    this.clonedItem = null;
  }


  deleteTruck(truck: any) {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove truck ${truck.truck_number}?`,
      header: 'Confirm Removal',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        this.dataServiceService.removeTruck(truck.truck_id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success', 
              detail: 'Truck removed successfully'
            });
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to remove truck'
            });
            console.error('Remove error:', error);
          }
        });
      }
    });
   }


   deleteTrailer(trailer: any) {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove trailer ${trailer.trailer_number}?`,
      header: 'Confirm Removal',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: ' No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      
      accept: () => {
        this.dataServiceService.removeTrailer(trailer.trailer_id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Trailer removed successfully'
            });
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to remove trailer'
            });
            console.error('Remove error:', error);
          }
        });
      }
    });
}

toggleVanStatus(trailer: Trailers) {
  // Toggle between Loaded and Empty
  const newStatus = trailer.status === 'Loaded' ? 'Empty' : 'Loaded';
  
  // Update the trailer status
  const updateData = {
    status: newStatus
  };
  
  this.dataServiceService.updateTrailer(trailer.trailer_id, updateData).subscribe({
    next: () => {
      // Update the local trailer object
      trailer.status = newStatus;
      
      this.messageService.add({
        severity: 'success',
        summary: 'Status Updated',
        detail: `Trailer ${trailer.trailer_number} is now ${newStatus}`
      });
    },
    error: (error) => {
      console.error('Error updating trailer status:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update trailer status'
      });
    }
  });
}

updateTruck() {
  if (this.truckForm.valid) {
    // Get form values
    const formData = {
      truck_id: this.selectedTruck.truck_id,
      truck_number: this.truckForm.value.truck_number,
      location_id: this.truckForm.value.location_id,
      status: this.truckForm.value.status,
      email: this.truckForm.value.email
    };
    
    // Call the service to update
    this.dataServiceService.updateTruck(formData.truck_id, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Truck updated successfully'
        });
        // Refresh data
        this.dataServiceService.getAllTrucks().subscribe();
        // Close dialog
        this.editTruckDialog = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update truck'
        });
      }
    });
  }
}

customSort(field: string, event: any) {
  if (field === 'trailer_number') {
  event.data.sort((data1: any, data2: any) => {
    let value1 = parseInt(data1.trailer_number) || 0;
    let value2 = parseInt(data2.trailer_number) || 0;
    let result = null;

    if (value1 < value2)
      result = -1;
    else if (value1 > value2)
      result = 1;
    else
      result = 0;

    return (event.order * result);
  });
} else if (field === 'truck_number') {
  event.data.sort((data1: any, data2: any) => {
    // Extract numeric part from truck numbers (handle "K" suffix)
    let str1 = data1.truck_number.toString();
    let str2 = data2.truck_number.toString();
    
    let value1 = parseInt(str1.replace(/[^\d]/g, '')) || 0;
    let value2 = parseInt(str2.replace(/[^\d]/g, '')) || 0;
    
    // Check if trucks have "K" suffix for secondary sorting
    let hasK1 = str1.toUpperCase().includes('K');
    let hasK2 = str2.toUpperCase().includes('K');
    
    let result = null;

    if (value1 < value2) {
      result = -1;
    } else if (value1 > value2) {
      result = 1;
    } else {
      // Same number - sort by K suffix (regular numbers come before K versions)
      if (!hasK1 && hasK2) {
        result = -1;
      } else if (hasK1 && !hasK2) {
        result = 1;
      } else {
        result = 0;
      }
    }

    return (event.order * result);
  });
}
  }
}